import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";

export const AVAILABLE_BUTTONS = {
  editAppointment: "edit-appointment",
  joinMeeting: "join-meeting",
  cancelAppointment: "cancel-appointment",
  noShow: "mark-as-noshow",
  patientCheckIn: "patient-check-in",
  patientCheckOut: "patient-checked-in",
};

const requestSchema = yup.object({
  appointmentId: yup.number().required(),
});

const responseSchema = yup.object({
  id: yup.number().required(),

  clinic: yup
    .object({
      id: yup.number().required(),
      name: yup.string().required(),
    })
    .required(),

  provider: yup
    .object({
      id: yup.number().required(),
      name: yup.string().required(),
    })
    .required(),

  additionalProvider: yup
    .object({
      id: yup.number().required(),
      name: yup.string().required(),
    })
    .nullable(),

  additionalProviderId: yup.number().nullable(),

  patientId: yup.number().nullable(),

  appointmentType: yup.string().required(),

  status: yup.string().nullable(),

  duration: yup.number().required(),

  date: yup.string().required(),

  isOutOfOffice: yup.bool().required(),

  services: yup.array().of(
    yup.object({
      id: yup.number().required(),
      name: yup.string().required(),
    }),
  ),

  availableButtons: yup.array().of(yup.string()).required(),

  examStatus: yup.string().nullable(),

  isCancellationPolicyEnabled: yup.bool().required(),

  isDepositCharged: yup.bool().required(),

  isAdditionalProviderAppointment: yup.bool().required(),

  patient: yup
    .object({
      id: yup.number().required(),
      fullName: yup.string().required(),
      type: yup.string().nullable(),
      isMonthlyMembership: yup.bool().required(),
      memberType: yup.string().nullable(),
      memberships: yup
        .array()
        .of(
          yup.object({
            color: yup.string().nullable(),
            onHold: yup.bool().required(),
          }),
        )
        .required(),
    })
    .nullable(),

  bookingDateTime: yup.string().nullable(),

  userLogs: yup
    .array()
    .of(
      yup.object({
        action: yup.string().required(),
        child: yup.string().nullable(),
        created: yup.string().required(),
        user: yup
          .object({
            firstname: yup.string().required(),
            lastname: yup.string().required(),
          })
          .nullable(),
      }),
    )
    .required(),

  booking: yup
    .object({
      bookedBy: yup.string().nullable(),
      user: yup
        .object({
          firstname: yup.string().nullable(),
          lastname: yup.string().nullable(),
        })
        .nullable(),
      email: yup.string().nullable(),
      phone: yup.string().nullable(),
      notes: yup.string().nullable(),
    })
    .required(),

  source: yup.string().nullable(),

  isConsentsCompleted: yup.number().required(),

  isQuestionnairesCompleted: yup.number().required(),

  isMainProvider: yup.bool().required(),

  providersForChange: yup.array().of(
    yup
      .object({
        id: yup.number().required(),
        fullName: yup.string().required(),
      })
      .required(),
  ),

  packageId: yup.number().required(),

  appointmentServices: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        name: yup.string().required(),
        duration: yup.number().required(),
      }),
    )
    .required(),

  package: yup
    .object({
      name: yup.string().required(),
      hours: yup.number().required(),
      mins: yup.number().required(),
    })
    .nullable(),

  endTime: yup.string().nullable(),

  isConfirmed: yup.bool().required(),

  lastCancelled: yup
    .object({
      date: yup.string().nullable(),
    })
    .nullable(),

  meetingUrl: yup.string().nullable(),

  meetingType: yup.string().nullable(),

  outOfOffice: yup
    .object({
      date: yup.string().required(),
      startTime: yup.string().required(),
      endTime: yup.string().required(),
    })
    .nullable(),

  mainAppointmentId: yup.number().nullable(),
});

const composeResponse = (res) => {
  const details = res?.data?.data?.appointment_details || {};
  const data = res?.data?.data || {};

  return {
    id: details.id,

    clinic: {
      id: details.clinic?.id,
      name: details.clinic?.clinic_name,
    },

    provider: {
      id: details.provider?.id,
      name: details.provider?.full_name,
    },

    additionalProvider: details.additional_provider
      ? {
          id: details.additional_provider.id,
          name: details.additional_provider.full_name,
        }
      : null,

    additionalProviderId: details.additional_provider?.id || null,

    patientId: details.patient_id || null,

    appointmentType: details.appointment_type,

    status: details.status || null,

    duration: details.duration,

    date: details.appointment_datetime,

    isOutOfOffice: Boolean(details.out_of_office),

    services: details?.services || [],

    availableButtons: data.available_buttons || [],

    examStatus: data.exam_status || null,

    isCancellationPolicyEnabled: Boolean(details.cancellation_policy),

    isDepositCharged: Boolean(details.deposit_charged),

    isAdditionalProviderAppointment:
      details.appointment_type === "virtual" && !details.is_main_provider,

    patient: details.patient
      ? {
          id: details.patient.id,
          fullName: details.patient.full_name,
          type: data.patient_type || null,
          isMonthlyMembership: Boolean(details.patient.is_monthly_membership),
          memberType: details.patient.member_type || null,
          memberships:
            data.patient_memberships?.map((m) => ({
              color: m.color || null,
              onHold: Boolean(m.on_hold),
            })) || [],
          state: details.patient.state || null,
          dateOfBirth: details.patient.date_of_birth || null,
        }
      : null,

    bookingDateTime: details.appointment_booking?.booking_datetime || null,

    userLogs: data.appointment_user_log || [],

    booking: {
      bookedBy: details.appointment_booking?.booked_by || null,
      user: details.appointment_booking?.user || {},
      email: details.appointment_booking?.email || null,
      phone: details.appointment_booking?.phone || null,
      notes: details.appointment_booking?.appointment_notes || "",
    },

    source: details.appointment_source || null,

    isConsentsCompleted: data.is_consent_completed,

    isQuestionnairesCompleted: data.is_questionnaire_completed,

    isMainProvider: Boolean(details.is_main_provider),

    providersForChange:
      data.providers_for_change_provider?.map((p) => ({
        id: p.id,
        fullName: p.full_name,
      })) || [],

    packageId: details.package_id || 0,

    appointmentServices:
      details.appointment_services?.map((s) => ({
        id: s.id,
        name: s.service?.name || "",
        duration: s.duration,
      })) || [],

    package: details.package
      ? {
          name: details.package.name,
          hours: details.package.hours || 0,
          mins: details.package.mins || 0,
        }
      : null,

    endTime: details.appointment_endtime || null,

    isConfirmed: Boolean(details.is_confirmed),

    lastCancelled: data.last_canceled_appointment
      ? {
          date: data.last_canceled_appointment.appointment_datetime || null,
        }
      : null,

    meetingUrl: details.meeting_url || null,

    meetingType: details.meeting_type || null,

    outOfOffice: details.out_of_office
      ? {
          date: details.date,
          startTime: details.start_time,
          endTime: details.end_time,
        }
      : null,

    mainAppointmentId: details.main_appointment_id || null,

    gfeOnFile: details.gfe_on_file,

    gfeExpired: details.gfe_expired,

    gfeIconColor: details.gfe_icon_color,
  };
};

export function useAppointmentQuery({ payload, options = {} }) {
  return useQuery({
    queryKey: [QUERY_KEYS.appointments.appointment, payload.appointmentId],
    queryFn: async () => {
      const req = requestSchema.validateSync(payload, {
        strict: true,
      });
      const res = await http.get(
        HTTP_ENDPOINTS.appointments.getAppointment(req.appointmentId),
      );
      return responseSchema.validateSync(composeResponse(res), {
        strict: true,
      });
    },
    ...options,
  });
}
